import React, {
  forwardRef, useState, useRef, useImperativeHandle, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import FormValidation from '../formEnums/formValidation';
import FormInputTypes from '../formEnums/formInputTypes';

let updateTimeout=null;
const DEFAULT_COLON = ':';
const DEFAULT_VALUE_SHORT = `00${DEFAULT_COLON}00`;
const DEFAULT_VALUE_FULL = `00${DEFAULT_COLON}00${DEFAULT_COLON}00`;
export function isNumber<T>(value){
  const number = Number(value);
  return !isNaN(number) && String(value) === String(number);
}

export function formatTimeItem(value){
  return `${value || ''}00`.substr(0, 2);
}

export function validateTimeAndCursor(
  showSeconds = true,
  value = '',
  defaultValue = '',
  colon = DEFAULT_COLON,
  cursorPosition = 0
) {
  const [oldH, oldM, oldS] = defaultValue.split(colon);

  let newCursorPosition = Number(cursorPosition);
  let [newH, newM, newS] = String(value).split(colon);
  newH = formatTimeItem(newH);
  if (Number(newH[0]) > 2) {
    newH = oldH;
    newCursorPosition -= 1;
  } else if (Number(newH[0]) === 2) {
    if (Number(oldH[0]) === 2 && Number(newH[1]) > 3) {
      newH = `2${oldH[1]}`;
      newCursorPosition -= 2;
    } else if (Number(newH[1]) > 3) {
      newH = '23';
    }
  }

  newM = formatTimeItem(newM);
  if (Number(newM[0]) > 5) {
    newM = oldM;
    newCursorPosition -= 1;
  }

  if (showSeconds) {
    newS = formatTimeItem(newS);
    if (Number(newS[0]) > 5) {
      newS = oldS;
      newCursorPosition -= 1;
    }
  }

  const validatedValue = defaultProps.showSeconds ? `${newH}${colon}${newM}${colon}${newS}` : `${newH}${colon}${newM}`;

  return [validatedValue, newCursorPosition];
}

const defaultProps={
  showSeconds: true,
  input: null,
  style: {},
  colon: DEFAULT_COLON
};
/**
 * Basic HTML input
 */
const InputTime = forwardRef((props, ref) => {
  const {
    id,
    name,
    type,
    validation,
    placeholder,
    value,
    onChange,
    className,
    disabled,
    separator,
    useSeconds
  } = props;
  const inputRef = useRef();

  const _showSeconds = Boolean(defaultProps.showSeconds);
  const _defaultValue = _showSeconds ? DEFAULT_VALUE_FULL : DEFAULT_VALUE_SHORT;
  const _colon = props.separator? separator : DEFAULT_COLON;
  const [validatedTime] = validateTimeAndCursor(_showSeconds, props.value, _defaultValue, _colon);

  const [disabledState, setDisabledState] = useState();
  const [colon,setColon] =useState(":");
  const [maxLength,setMaxLength]=useState(_defaultValue.length);
  const [mySeparator,setMySeparator] =useState(separator? separator : DEFAULT_COLON);
  const [valueState, setValueState] = useState(!value ? '00'+mySeparator+'00'+(useSeconds?mySeparator+'00':'') : value);
  const [maxH,maxM,maxS]=[24,60,60];
  
  const handleChange = (event) => {
    const oldValue = valueState;
    const inputEl = event.target;
    const inputValue = inputEl.value;
    const position = inputEl.selectionEnd || 0;
    const isTyped = inputValue.length > oldValue.length;
    const cursorCharacter = inputValue[position - 1];
    const addedCharacter = isTyped ? cursorCharacter : null;
    const removedCharacter = isTyped ? null : oldValue[position];
    const replacedSingleCharacter = inputValue.length === oldValue.length ? oldValue[position - 1] : null;
    
   
    let newValue = oldValue;
    let newPosition = position;
    if (addedCharacter !== null) {
      if (position >maxLength) {
        newPosition = maxLength;
      } else if ((position === 3 || position === 6) && addedCharacter === mySeparator) {
        newValue = `${inputValue.substr(0, position - 1)}${mySeparator}${inputValue.substr(position + 1)}`;
      } else if ((position === 3 || position === 6) && isNumber(addedCharacter)) {
        newValue = `${inputValue.substr(0, position - 1)}${mySeparator}${addedCharacter}${inputValue.substr(position + 2)}`;
        newPosition = position + 1;
      } else if (isNumber(addedCharacter)) {
        // user typed a number
        newValue = inputValue.substr(0, position - 1) + addedCharacter + inputValue.substr(position + 1);
        if (position === 2 || position === 5) {
          newPosition = position + 1;
        }
      } else {
        // if user typed NOT a number, then keep old value & position
        newPosition = position - 1;
      }
    } else if (replacedSingleCharacter !== null) {
      // user replaced only a single character
      if (isNumber(cursorCharacter)) {
        if (position - 1 === 2 || position - 1 === 5) {
          newValue = `${inputValue.substr(0, position - 1)}${mySeparator}${inputValue.substr(position)}`;
        } else {
          newValue = inputValue;
        }
      } else {
        // user replaced a number on some non-number character
        newValue = oldValue;
        newPosition = position - 1;
      }
    } else if (typeof cursorCharacter !== 'undefined' && cursorCharacter !== mySeparator && !isNumber(cursorCharacter)) {
      // set of characters replaced by non-number
      newValue = oldValue;
      newPosition = position - 1;
    } else if (removedCharacter !== null) {
      if ((position === 2 || position === 5) && removedCharacter === mySeparator) {
        newValue = `${inputValue.substr(0, position - 1)}0${mySeparator}${inputValue.substr(position)}`;
        newPosition = position - 1;
      } else {
        // user removed a number
        newValue = `${inputValue.substr(0, position)}0${inputValue.substr(position)}`;
      }
    }
    let targetLength=event.target.value.length;
   
    let new_position=position;
    if(targetLength >newValue.length){
      //adding
    if(position==2|| position==5 ){
      new_position++;
    }  
    }else{
      //removing
      if(position==3 || position==5){
        new_position--;
      }
    }
    let validateTime=validateTimeAndCursor(true,newValue,oldValue,mySeparator);
    
    inputRef.current.value=validateTime[0];
    setValueState(validateTime[0]);
    inputRef.current.selectionStart = new_position;
    inputRef.current.selectionEnd = new_position;
    onChange({ target: { value: validateTime[0]} });
  };

  const onEventChange=(callback)=>{
    if(updateTimeout)
      clearTimeout(updateTimeout);

    updateTimeout=setTimeout(()=>{
      callback();
    },800);
  }
  const zeroPad=(num, places)=> {
    var zero = places - num.toString().length + 1;
    return Array(+(zero > 0 && zero)).join("0") + num;
  }
  const getNumber=(value,operation,max)=>{
    value=parseInt(value)+operation;
      if(value<0){
        value=max+value;
      }
      if(value>max-1){
        value=0;
      }
      return zeroPad(value,2);
  }
  const calcNumber=(h,m,s,position,mode)=>{
    let operation=0;
    if(mode=="up"){
      operation=1;
    }else{
      operation=-1;
    }
    

    if(position>=7 && position<=8){
        s=getNumber(s,operation,maxS);
    }
    if(position>=4 && position<=5){
      m=getNumber(m,operation,maxM);
    }
    if(position>=0 && position<=2){
      h=getNumber(h,operation,maxH);
    }
  
    return h+mySeparator+m+mySeparator+s;
  }

  const handleKey=(event)=>{
    const inputEl = event.target;
    const position = inputEl.selectionEnd || 0;
    const [oldH, oldM, oldS] = event.target.value.split(mySeparator);
    
    if(event.keyCode==38 || event.keyCode==40){
      event.preventDefault();
      let newvalue="";
      if(event.keyCode==38){
        newvalue=calcNumber(oldH,oldM,oldS,position,"up");
      }else if(event.keyCode==40){
        newvalue=calcNumber(oldH,oldM,oldS,position,"down");
      }
      setValueState(newvalue);
      inputRef.current.value=newvalue;

      inputRef.current.selectionStart = position;
      inputRef.current.selectionEnd = position;
      onChange({ target: { value: newvalue} }); 
    }

  }

  useImperativeHandle(ref, () => ({
    updateValue(newValue) {
      setValueState(newValue === null ? '' : newValue);
    },
  }));
    return (
      <input
        id={id}
        type="text"
        className={`form-control border-${validation}  ${className}`}

        ref={inputRef}
        value={valueState}
        onChange={handleChange}
        onKeyDown={handleKey}
        // style={{width: validatedTime.showSeconds ? 254 : 235}}
      />
    );
  // }
});

InputTime.propTypes = {
  /**
   * id of input field
   */
  id: PropTypes.string,
  /**
   * Name of input field
   */
  name: PropTypes.string.isRequired,
  /**
   * HTML input type, it is a FormInputTypes.TEXT_INPUT enum
   */
  type: PropTypes.oneOf(Object.values(FormInputTypes.TEXT_INPUT)),
  /**
   * Input form status, it is a FormValidation enum
   */
  validation: PropTypes.oneOf(Object.values(FormValidation)),
  /**
   * Placeholder inside the HTML input
   */
  placeholder: PropTypes.string,
  /**
   * Set the value inside the HTML input
   */
  value: PropTypes.node,
  /**
   * Function to bind onChange HTML functionality
   */
  onChange: PropTypes.func,
  /**
   * custom css class
   */
  className: PropTypes.string,
  /**
   * The input regular expression to control
   */
  pattern: PropTypes.instanceOf(RegExp),
  /**
   * Enable browser autocomplete
   */
  autoComplete: PropTypes.bool,
  /**
   * Enable browser autocomplete
   */
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  onBlur: PropTypes.func,
};

InputTime.defaultProps = {
  id: '',
  type: FormInputTypes.TEXT_INPUT.TEXT,
  validation: FormValidation.PENDING,
  className: '',
  placeholder: '',
  value: '',
  onChange: () => {},
  pattern: undefined,
  autoComplete: true,
  disabled: false,
  autoFocus: false,
  onBlur: () => {},
};

export default InputTime;
